import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, ChevronLeft, ChevronRight, Flag, Plus, X } from "lucide-react";
import { cmdApi } from "../api";
import { usePermissions } from "../hooks/usePermissions";
import { formatINR, fmtDate, fmtDateTime, HEALTH_LABEL, STAGE_LABEL } from "../format";
import { computeEntry, fmtPct } from "../fincalc";

function Sheet({ onClose, title, children, testId }) {
  return (
    <>
      <div className="cmd-sheet-backdrop" onClick={onClose} />
      <div className="cmd-sheet" role="dialog" aria-modal="true" data-testid={testId}>
        <div className="cmd-sheet-head">
          <h2>{title}</h2>
          <button className="cmd-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="cmd-sheet-body">{children}</div>
      </div>
    </>
  );
}

export default function CmdReview() {
  const { can, role } = usePermissions();
  const canEditBoard = can("board", "edit");
  const canEditDecisions = can("decisions", "edit");
  const canEditPortfolio = can("portfolio", "edit");
  const navigate = useNavigate();

  const [roles, setRoles] = useState([]);
  const [viewAs, setViewAs] = useState("");
  const [sessions, setSessions] = useState(null);
  const [session, setSession] = useState(null);   // {id, order:[ids]}
  const [idx, setIdx] = useState(0);
  const [detail, setDetail] = useState(null);
  const [updateText, setUpdateText] = useState("");
  const [milestoneEdit, setMilestoneEdit] = useState(false);
  const [milestoneVal, setMilestoneVal] = useState("");
  const [milestoneDate, setMilestoneDate] = useState("");
  const [addActionOpen, setAddActionOpen] = useState(false);
  const [actionTitle, setActionTitle] = useState("");
  const [actionDue, setActionDue] = useState("");
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [decTitle, setDecTitle] = useState("");
  const [decContext, setDecContext] = useState("");
  const [stats, setStats] = useState({ health: 0, updates: 0, actions: 0, decisions: 0 });
  const [covered, setCovered] = useState([]);
  const [finished, setFinished] = useState(null);
  const [busy, setBusy] = useState(false);

  const currentId = session?.order?.[idx];

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cfg }, { data: sess }] = await Promise.all([
          cmdApi.get("/roles-config"), cmdApi.get("/review-sessions"),
        ]);
        setRoles(cfg.roles || []);
        setSessions(sess.items || []);
      } catch { setSessions([]); }
    })();
  }, []);

  const loadDetail = async (id) => {
    try {
      const { data } = await cmdApi.get(`/initiatives/${id}`);
      setDetail(data);
      setMilestoneVal(data.next_milestone || "");
      setMilestoneDate(data.milestone_date || "");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not load initiative.");
    }
  };
  useEffect(() => { if (currentId) loadDetail(currentId); }, [currentId]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await cmdApi.post("/review-sessions", { viewing_as: viewAs || null });
      setSession(data); setIdx(0); setCovered([]);
      setStats({ health: 0, updates: 0, actions: 0, decisions: 0 });
      setFinished(null);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Could not start review.");
    } finally { setBusy(false); }
  };

  const markCovered = (id) => setCovered((c) => (c.includes(id) ? c : [...c, id]));

  const setHealth = async (h) => {
    if (!detail) return;
    try {
      const { data } = await cmdApi.post(`/initiatives/${detail.id}/health`, { health: h });
      toast.success(data.update.text);
      setStats((s) => ({ ...s, health: s.health + 1 }));
      markCovered(detail.id);
      loadDetail(detail.id);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not set health."); }
  };

  const postUpdate = async () => {
    if (!updateText.trim() || !detail) return;
    try {
      await cmdApi.post("/updates", { initiative_id: detail.id, text: updateText.trim() });
      toast.success("Update posted");
      setUpdateText("");
      setStats((s) => ({ ...s, updates: s.updates + 1 }));
      markCovered(detail.id);
      loadDetail(detail.id);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not post update."); }
  };

  const saveMilestone = async () => {
    if (!detail) return;
    try {
      await cmdApi.patch(`/initiatives/${detail.id}`, {
        next_milestone: milestoneVal || null, milestone_date: milestoneDate || null,
      });
      toast.success("Milestone updated");
      setMilestoneEdit(false);
      loadDetail(detail.id);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not save milestone."); }
  };

  const tickAction = async (a, checked) => {
    try {
      await cmdApi.patch(`/actions/${a.id}`, { status: checked ? "done" : "open" });
      markCovered(detail.id);
      loadDetail(detail.id);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not update action."); }
  };

  const addAction = async () => {
    if (!actionTitle.trim() || !detail) return;
    try {
      await cmdApi.post("/actions", {
        title: actionTitle.trim(), initiative_id: detail.id,
        due_date: actionDue || null, owner_role: detail.owner_role,
      });
      toast.success("Action added");
      setActionTitle(""); setActionDue(""); setAddActionOpen(false);
      setStats((s) => ({ ...s, actions: s.actions + 1 }));
      markCovered(detail.id);
      loadDetail(detail.id);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not add action."); }
  };

  const vote = async (decId, v) => {
    try {
      await cmdApi.post(`/decisions/${decId}/vote`, { vote: v });
      loadDetail(detail.id);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Vote failed."); }
  };

  const raiseDecision = async () => {
    if (!decTitle.trim() || !detail) return;
    try {
      await cmdApi.post("/decisions", { title: decTitle.trim(), initiative_id: detail.id, context: decContext || null });
      toast.success("Decision raised");
      setDecTitle(""); setDecContext(""); setRaiseOpen(false);
      setStats((s) => ({ ...s, decisions: s.decisions + 1 }));
      markCovered(detail.id);
      loadDetail(detail.id);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not raise decision."); }
  };

  const finish = async () => {
    const coveredIds = [...new Set([...covered, detail?.id].filter(Boolean))];
    try {
      const { data } = await cmdApi.patch(`/review-sessions/${session.id}`, {
        covered_initiative_ids: coveredIds,
        stats, ended: true,
      });
      setFinished({ ...data, stats });
      setSession(null);
      const { data: sess } = await cmdApi.get("/review-sessions");
      setSessions(sess.items || []);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Could not save session."); }
  };

  // ── Finish summary ──────────────────────────────────────────────────────────
  if (finished) {
    const s = finished.stats || {};
    return (
      <section data-testid="review-summary">
        <div className="cmd-review-card">
          <div className="cmd-review-body cmd-session-summary">
            <div className="big">{finished.covered?.length || 0}</div>
            <p className="cmd-muted" style={{ marginTop: 0 }}>initiatives reviewed</p>
            <p style={{ fontSize: 15 }}>
              {s.health || 0} health changes · {s.updates || 0} updates posted · {s.actions || 0} actions added · {s.decisions || 0} decisions raised
            </p>
            <button className="cmd-btn cmd-btn-primary" onClick={() => navigate("/cmd/board")} data-testid="open-board">
              Open the Board
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ── Start screen ────────────────────────────────────────────────────────────
  if (!session) {
    return (
      <section data-testid="cmd-review">
        <div className="cmd-toolbar" style={{ alignItems: "flex-end" }}>
          <div className="cmd-field" style={{ margin: 0, minWidth: 220 }}>
            <label style={{ marginBottom: 4 }}>Viewing as</label>
            <select className="cmd-input" value={viewAs} onChange={(e) => setViewAs(e.target.value)} data-testid="review-viewing-as">
              <option value="">Whole company</option>
              {roles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button className="cmd-btn cmd-btn-primary" onClick={start} disabled={busy} data-testid="start-review">
            <Play size={15} style={{ marginRight: 6 }} />{busy ? "Starting…" : "Start review"}
          </button>
        </div>
        <h3 className="cmd-vertical-head" style={{ marginTop: 24 }}>Past sessions</h3>
        {sessions === null ? <p className="cmd-muted">Loading…</p>
          : sessions.length === 0 ? (
            <div className="cmd-empty" data-testid="review-empty">
              <h3>No reviews yet</h3>
              <p>Start a review to walk every initiative, riskiest first.</p>
            </div>
          ) : (
            sessions.map((sn) => (
              <div key={sn.id} className="cmd-mini-row" data-testid={`session-${sn.id}`}>
                <span>{sn.started_by_name} · {sn.covered.length} of {sn.order.length} covered{sn.viewing_as ? ` · as ${sn.viewing_as}` : ""}</span>
                <span className="cmd-muted" style={{ fontSize: 12 }}>
                  {fmtDateTime(sn.started_at)}{sn.ended_at ? ` → ${fmtDateTime(sn.ended_at)}` : " · unfinished"}
                </span>
              </div>
            ))
          )}
      </section>
    );
  }

  // ── In-session screen ───────────────────────────────────────────────────────
  const d = detail;
  const waiting = (d?.depends_on || []).filter((x) => x.stage !== "running");
  const latestFig = d?.financial_summary?.[0];
  const figs = latestFig ? computeEntry(latestFig) : null;
  const healthCls = d?.health ? `h-${d.health}` : "h-none";

  return (
    <section data-testid="review-session">
      <div className="cmd-review-card">
        <div className={`cmd-review-bar ${healthCls}`} />
        <div className="cmd-review-body">
          <div className="cmd-review-progress" data-testid="review-progress">
            Reviewing {idx + 1} of {session.order.length} · riskiest first
          </div>
          {d && (
            <>
              <h2 className="cmd-review-name">{d.name}</h2>
              <div className="cmd-review-tags">
                <span className="cmd-chip">{d.vertical}</span>
                <span className={`cmd-pill stage-${d.stage}`}>{STAGE_LABEL[d.stage]}</span>
                {d.owner_role && <span className="cmd-chip">{d.owner_role}</span>}
                {d.health
                  ? <span className={`cmd-pill health-${d.health}`}>{HEALTH_LABEL[d.health]}</span>
                  : <span className="cmd-chip">Not rated</span>}
              </div>
              {d.key_metric && <p style={{ margin: "0 0 6px", fontSize: 14 }}><span className="cmd-muted">Key number:</span> {d.key_metric}</p>}
              {waiting.length > 0 && (
                <p className="cmd-init-waiting" data-testid="review-waiting" style={{ fontSize: 14 }}>
                  Waiting on {waiting.map((w) => w.name).join(", ")}
                </p>
              )}

              {figs && (
                <div className="cmd-fig-strip" data-testid="review-figures">
                  <div><div className="f">{latestFig.month}</div><div className="v">{formatINR(latestFig.revenue)}</div></div>
                  <div><div className="f">GM</div><div className="v">{fmtPct(figs.gm_pct)}</div></div>
                  <div><div className="f">EBITDA</div><div className="v">{formatINR(figs.ebitda)}</div></div>
                  <div><div className="f">Receivables</div><div className="v">{formatINR(latestFig.receivables)}</div></div>
                </div>
              )}

              <h4 style={{ margin: "16px 0 4px", fontSize: 14 }}>How is it going?</h4>
              <div className="cmd-health-btns">
                {[["ok", "On track"], ["watch", "Watch"], ["risk", "At risk"]].map(([k, label]) => (
                  <button key={k} className={`cmd-health-btn ${d.health === k ? `sel-${k}` : ""}`}
                    onClick={() => setHealth(k)} data-testid={`health-${k}`}>{label}</button>
                ))}
              </div>

              <h4 style={{ margin: "16px 0 6px", fontSize: 14 }}>What changed since the last review?</h4>
              {(d.updates || []).slice(0, 2).map((u) => (
                <div key={u.id} className="cmd-update" style={{ fontSize: 13 }}>
                  <div>{u.text}</div>
                  <div className="cmd-muted" style={{ fontSize: 12 }}>{u.created_by_name} · {fmtDateTime(u.created_at)}</div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <input className="cmd-input" placeholder="Post an update…" value={updateText}
                  onChange={(e) => setUpdateText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && postUpdate()}
                  data-testid="review-update-input" />
                <button className="cmd-btn" onClick={postUpdate} disabled={!updateText.trim()} data-testid="review-post-update">Post update</button>
              </div>

              <div style={{ marginTop: 18 }}>
                <h4 style={{ margin: "0 0 6px", fontSize: 14 }}>Next milestone</h4>
                {milestoneEdit ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <input className="cmd-input" style={{ flex: 2 }} value={milestoneVal}
                      onChange={(e) => setMilestoneVal(e.target.value)} data-testid="review-milestone-text" />
                    <input type="date" className="cmd-input" style={{ flex: 1 }} value={milestoneDate}
                      onChange={(e) => setMilestoneDate(e.target.value)} data-testid="review-milestone-date" />
                    <button className="cmd-btn cmd-btn-primary" onClick={saveMilestone} data-testid="review-milestone-save">Save</button>
                    <button className="cmd-btn cmd-btn-ghost" onClick={() => setMilestoneEdit(false)}>Cancel</button>
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: 14 }}>
                    {d.next_milestone
                      ? <>{d.next_milestone}{d.milestone_date ? ` by ${fmtDate(d.milestone_date)}` : ""}</>
                      : <span className="cmd-muted">No next milestone set</span>}
                    {canEditPortfolio && (
                      <button className="cmd-link" style={{ width: "auto", padding: "0 8px" }}
                        onClick={() => setMilestoneEdit(true)} data-testid="review-milestone-edit">Edit</button>
                    )}
                  </p>
                )}
              </div>

              <div style={{ marginTop: 18 }}>
                <h4 style={{ margin: "0 0 6px", fontSize: 14 }}>Open actions ({d.open_actions})</h4>
                {(d.actions || []).map((a) => (
                  <div key={a.id} className="cmd-mini-row" style={{ fontSize: 13.5 }}>
                    <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      {canEditBoard && (
                        <input type="checkbox" onChange={(e) => tickAction(a, e.target.checked)}
                          aria-label={`Done: ${a.title}`} data-testid={`review-tick-${a.id}`} />
                      )}
                      {a.title}
                    </span>
                    <span className={`cmd-muted ${a.late ? "cmd-danger-text" : ""}`} style={{ fontSize: 12 }}>
                      {a.due_date ? fmtDate(a.due_date) : "No date"}{a.late ? " · late" : ""}
                    </span>
                  </div>
                ))}
                {canEditBoard && (addActionOpen ? (
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <input className="cmd-input" style={{ flex: 2 }} placeholder="What needs to happen" value={actionTitle}
                      onChange={(e) => setActionTitle(e.target.value)} data-testid="review-action-title" />
                    <input type="date" className="cmd-input" style={{ flex: 1 }} value={actionDue}
                      onChange={(e) => setActionDue(e.target.value)} data-testid="review-action-due" />
                    <button className="cmd-btn cmd-btn-primary" onClick={addAction} data-testid="review-action-save">Add</button>
                    <button className="cmd-btn cmd-btn-ghost" onClick={() => setAddActionOpen(false)}>Cancel</button>
                  </div>
                ) : (
                  <button className="cmd-link" style={{ width: "auto", padding: "6px 0" }}
                    onClick={() => setAddActionOpen(true)} data-testid="review-add-action">
                    <Plus size={13} style={{ marginRight: 4, verticalAlign: -2 }} />Add action
                  </button>
                ))}
              </div>

              <div style={{ marginTop: 18 }}>
                <h4 style={{ margin: "0 0 6px", fontSize: 14 }}>Open decisions ({d.open_decisions})</h4>
                {(d.open_decisions_list || []).map((dec) => {
                  const counts = { approve: 0, hold: 0, reject: 0 };
                  for (const v of Object.values(dec.votes || {})) counts[v] = (counts[v] || 0) + 1;
                  return (
                    <div key={dec.id} style={{ padding: "8px 0", borderBottom: "1px dashed var(--cmd-line)" }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{dec.title}</div>
                      {canEditDecisions && (
                        <div className="cmd-vote-row" style={{ marginTop: 6 }}>
                          {["approve", "hold", "reject"].map((v) => (
                            <button key={v} className="cmd-vote-btn" onClick={() => vote(dec.id, v)}
                              data-testid={`review-vote-${v}-${dec.id}`}>
                              {v[0].toUpperCase() + v.slice(1)} <span className="cmd-vote-count">{counts[v]}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="cmd-review-actions-row">
                <button className="cmd-btn cmd-btn-ghost" disabled={idx === 0}
                  onClick={() => setIdx(idx - 1)} data-testid="review-prev">
                  <ChevronLeft size={15} style={{ marginRight: 4 }} />Previous
                </button>
                {canEditDecisions && (
                  <button className="cmd-btn" onClick={() => setRaiseOpen(true)} data-testid="review-raise-decision">
                    <Flag size={14} style={{ marginRight: 5 }} />Raise decision
                  </button>
                )}
                <div className="spacer" />
                {idx < session.order.length - 1 ? (
                  <button className="cmd-btn cmd-btn-primary"
                    onClick={() => { markCovered(detail.id); setIdx(idx + 1); }}
                    data-testid="review-next">
                    Next initiative <ChevronRight size={15} style={{ marginLeft: 4 }} />
                  </button>
                ) : (
                  <button className="cmd-btn cmd-btn-primary" onClick={finish} data-testid="review-finish">
                    Finish review
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {raiseOpen && d && (
        <Sheet onClose={() => setRaiseOpen(false)} title="Raise decision" testId="review-decision-sheet">
          <div className="cmd-field">
            <label>Decision (one line)</label>
            <input className="cmd-input" value={decTitle} onChange={(e) => setDecTitle(e.target.value)}
              maxLength={300} data-testid="review-dec-title" />
          </div>
          <div className="cmd-field">
            <label>Context, options, recommendation</label>
            <textarea className="cmd-input" rows={4} value={decContext}
              onChange={(e) => setDecContext(e.target.value)} data-testid="review-dec-context" />
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="cmd-btn cmd-btn-ghost" onClick={() => setRaiseOpen(false)}>Cancel</button>
            <button className="cmd-btn cmd-btn-primary" onClick={raiseDecision} data-testid="review-dec-save">Raise decision</button>
          </div>
        </Sheet>
      )}
    </section>
  );
}
